@file:NoLiveLiterals

package com.example.borshi.web.wallet

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlin.js.Promise
import kotlin.random.Random

/**
 * UI state for wallet display
 */
sealed class WalletUiState {
    object Loading : WalletUiState()
    data class Ready(val wallet: Wallet) : WalletUiState()
    data class NotCreated(val message: String = "Add money to activate your wallet") : WalletUiState()
    data class Error(val message: String) : WalletUiState()
}

/**
 * UI state for transaction history
 */
sealed class TransactionUiState {
    object Loading : TransactionUiState()
    data class Ready(
        val transactions: List<WalletTransaction>,
        val hasMore: Boolean = false
    ) : TransactionUiState()
    object Empty : TransactionUiState()
    data class Error(val message: String) : TransactionUiState()
}

/**
 * Filter for transaction list
 */
enum class TransactionFilter {
    ALL,
    PURCHASES,
    REFUNDS,
    TOPUPS
}

/**
 * ViewModel for wallet feature
 * Manages wallet state and orchestrates repository operations
 */
class WalletViewModel(
    private val repository: WalletRepository,
    private val userId: String,
    private val customerName: String = "Customer",
    private val customerPhone: String = "",
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Default + SupervisorJob())
) {

    // Wallet state
    private val _walletUiState = MutableStateFlow<WalletUiState>(WalletUiState.Loading)
    val walletUiState: StateFlow<WalletUiState> = _walletUiState.asStateFlow()

    // Transaction state
    private val _transactionUiState = MutableStateFlow<TransactionUiState>(TransactionUiState.Loading)
    val transactionUiState: StateFlow<TransactionUiState> = _transactionUiState.asStateFlow()

    // Filter state
    private val _filter = MutableStateFlow(TransactionFilter.ALL)
    val filter: StateFlow<TransactionFilter> = _filter.asStateFlow()

    // Operation result (for showing success/error messages)
    private val _operationResult = MutableStateFlow<OperationResult?>(null)
    val operationResult: StateFlow<OperationResult?> = _operationResult.asStateFlow()

    // Loading state for operations
    private val _isOperating = MutableStateFlow(false)
    val isOperating: StateFlow<Boolean> = _isOperating.asStateFlow()

    private var transactionJob: Job? = null
    private var lastTransactionTimestamp: Long? = null

    init {
        observeWallet()
        loadTransactions()
    }

    /**
     * Observe wallet changes in real-time
     */
    private fun observeWallet() {
        scope.launch {
            repository.observeWallet()
                .collect { wallet ->
                    _walletUiState.value = when {
                        wallet == null -> WalletUiState.NotCreated()
                        else -> WalletUiState.Ready(wallet)
                    }
                }
        }
    }

    /**
     * Load transactions with current filter
     */
    fun loadTransactions(reset: Boolean = false) {
        transactionJob?.cancel()

        if (reset) {
            lastTransactionTimestamp = null
            _transactionUiState.value = TransactionUiState.Loading
        }

        transactionJob = scope.launch {
            try {
                repository.observeTransactions(
                    limit = 20,
                    startAfter = lastTransactionTimestamp
                ).catch { e ->
                    console.error("Error loading transactions", e)
                    _transactionUiState.value = TransactionUiState.Error("Failed to load transactions")
                }.collect { transactions ->
                    val filtered = filterTransactions(transactions, _filter.value)

                    if (filtered.isEmpty() && lastTransactionTimestamp == null) {
                        _transactionUiState.value = TransactionUiState.Empty
                    } else {
                        val hasMore = transactions.size >= 20
                        _transactionUiState.value = TransactionUiState.Ready(filtered, hasMore)

                        if (transactions.isNotEmpty()) {
                            lastTransactionTimestamp = transactions.last().createdAt
                        }
                    }
                }
            } catch (e: Exception) {
                console.error("Error loading transactions", e)
                _transactionUiState.value = TransactionUiState.Error("Failed to load transactions")
            }
        }
    }

    /**
     * Set transaction filter
     */
    fun setFilter(filter: TransactionFilter) {
        _filter.value = filter
        loadTransactions(reset = true)
    }

    /**
     * Request a top-up via Cashfree.
     * Creates top-up intent, then opens Cashfree checkout. On successful payment,
     * the webhook or completeWalletTopUpIfPaid credits the wallet.
     */
    suspend fun requestTopUp(amount: Money): Boolean {
        if (_isOperating.value) return false

        _isOperating.value = true
        _operationResult.value = null

        return try {
            val idempotencyKey = generateIdempotencyKey("topup")
            val intentResult = repository.requestTopUp(
                userId = userId,
                amount = amount,
                idempotencyKey = idempotencyKey,
                paymentMethodId = null
            )

            intentResult.fold(
                onSuccess = { intent ->
                    if (customerPhone.isBlank()) {
                        _operationResult.value = OperationResult.Failure(
                            "Please add your phone number in profile to add money"
                        )
                        false
                    } else {
                        val amountRupees = amount.toDecimalString().toDoubleOrNull() ?: 0.0
                        val service = kotlinx.browser.window.asDynamic().cashfreeService
                        val hasCashfree = js("typeof window.cashfreeService !== 'undefined' && window.cashfreeService != null && typeof window.cashfreeService.initiateWalletTopUp === 'function'") as Boolean
                        if (!hasCashfree) {
                            _operationResult.value = OperationResult.Failure(
                                "Payment service not ready. Please refresh and try again."
                            )
                            false
                        } else {
                            @Suppress("UNCHECKED_CAST")
                            val promise = (service.initiateWalletTopUp(
                                intent.intentId,
                                amountRupees,
                                customerName,
                                customerPhone
                            ) as kotlin.js.Promise<dynamic>)
                            val result = promise.await()
                            val success = result.success == true
                            if (!success) {
                                _operationResult.value = OperationResult.Failure(
                                    (result.message as? String) ?: "Payment failed. Please try again."
                                )
                            }
                            success
                        }
                    }
                },
                onFailure = { e ->
                    _operationResult.value = OperationResult.Failure(
                        "Top-up failed: ${e.message}"
                    )
                    false
                }
            )
        } finally {
            _isOperating.value = false
        }
    }

    /**
     * Purchase with wallet
     */
    suspend fun purchaseWithWallet(
        planId: String,
        amount: Money,
        metadata: Map<String, String> = emptyMap()
    ): Boolean {
        if (_isOperating.value) return false

        _isOperating.value = true
        _operationResult.value = null

        return try {
            val idempotencyKey = generateIdempotencyKey("purchase-$planId")
            val result = repository.purchaseWithWallet(
                userId = userId,
                planId = planId,
                amount = amount,
                idempotencyKey = idempotencyKey,
                metadata = metadata
            )

            result.fold(
                onSuccess = { opResult ->
                    when (opResult) {
                        is WalletOperationResult.Success -> {
                            _operationResult.value = OperationResult.Success(
                                "Purchase successful: ${amount.format()}"
                            )
                            loadTransactions(reset = true)
                            true
                        }
                        is WalletOperationResult.Failure -> {
                            _operationResult.value = OperationResult.Failure(
                                getErrorMessage(opResult.error)
                            )
                            false
                        }
                    }
                },
                onFailure = { e ->
                    _operationResult.value = OperationResult.Failure(
                        "Purchase failed: ${e.message}"
                    )
                    false
                }
            )
        } finally {
            _isOperating.value = false
        }
    }

    /**
     * Request refund
     */
    suspend fun requestRefund(
        orderId: String,
        amount: Money,
        reason: String = ""
    ): Boolean {
        if (_isOperating.value) return false

        _isOperating.value = true
        _operationResult.value = null

        return try {
            val idempotencyKey = generateIdempotencyKey("refund-$orderId")
            val result = repository.refundToWallet(
                userId = userId,
                orderId = orderId,
                amount = amount,
                idempotencyKey = idempotencyKey,
                reason = reason
            )

            result.fold(
                onSuccess = { opResult ->
                    when (opResult) {
                        is WalletOperationResult.Success -> {
                            _operationResult.value = OperationResult.Success(
                                "Refund processed: ${amount.format()}"
                            )
                            loadTransactions(reset = true)
                            true
                        }
                        is WalletOperationResult.Failure -> {
                            _operationResult.value = OperationResult.Failure(
                                getErrorMessage(opResult.error)
                            )
                            false
                        }
                    }
                },
                onFailure = { e ->
                    _operationResult.value = OperationResult.Failure(
                        "Refund failed: ${e.message}"
                    )
                    false
                }
            )
        } finally {
            _isOperating.value = false
        }
    }

    /**
     * Clear operation result message
     */
    fun clearOperationResult() {
        _operationResult.value = null
    }

    /**
     * Filter transactions based on type
     */
    private fun filterTransactions(
        transactions: List<WalletTransaction>,
        filter: TransactionFilter
    ): List<WalletTransaction> {
        return when (filter) {
            TransactionFilter.ALL -> transactions
            TransactionFilter.PURCHASES -> transactions.filter {
                it.type == TransactionType.PURCHASE
            }
            TransactionFilter.REFUNDS -> transactions.filter {
                it.type == TransactionType.REFUND
            }
            TransactionFilter.TOPUPS -> transactions.filter {
                it.type == TransactionType.TOPUP
            }
        }
    }

    /**
     * Generate unique idempotency key
     */
    private fun generateIdempotencyKey(prefix: String): String {
        val timestamp = kotlin.js.Date.now().toLong()
        val random = Random.nextInt(10000, 99999)
        return "$prefix-$userId-$timestamp-$random"
    }

    /**
     * Get user-friendly error message
     */
    private fun getErrorMessage(error: WalletError): String {
        return when (error) {
            WalletError.InsufficientFunds -> "Insufficient wallet balance"
            WalletError.WalletNotFound -> "Wallet not found. Please add money first."
            WalletError.TransactionNotFound -> "Transaction not found"
            WalletError.InvalidAmount -> "Invalid amount"
            WalletError.DuplicateTransaction -> "This transaction was already processed"
            is WalletError.NetworkError -> "Network error: ${error.message}"
            is WalletError.UnknownError -> "Error: ${error.message}"
        }
    }

    /**
     * Clean up resources
     */
    fun dispose() {
        transactionJob?.cancel()
        scope.cancel()
    }
}

/**
 * Result of a wallet operation (for UI feedback)
 */
sealed class OperationResult {
    data class Success(val message: String) : OperationResult()
    data class Failure(val message: String) : OperationResult()
}
